import React, { useEffect, useState } from 'react';
import {
    eventService,
    EventParticipant,
    EventConfig,
    EventStats
} from '../../services/eventService';
import { useToast } from '../../components/Toast';
import { getMediaUrl } from '../../utils/media';
import './AdminEventsPage.css';

const AdminEventsPage: React.FC = () => {
    const { showToast } = useToast();

    // Tabs
    const [activeTab, setActiveTab] = useState<'participants' | 'config'>('participants');

    // Stats
    const [stats, setStats] = useState<EventStats | null>(null);

    // Participants
    const [participants, setParticipants] = useState<EventParticipant[]>([]);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);
    const [perPage] = useState(20);
    const [search, setSearch] = useState('');
    const [verifiedFilter, setVerifiedFilter] = useState('');
    const [prizeFilter, setPrizeFilter] = useState('');

    // Selection
    const [selectedIds, setSelectedIds] = useState<number[]>([]);
    const [selectAll, setSelectAll] = useState(false);

    // Config
    const [config, setConfig] = useState<EventConfig | null>(null);
    const [configLoading, setConfigLoading] = useState(false);
    const [savingConfig, setSavingConfig] = useState(false);

    // Modal
    const [previewImage, setPreviewImage] = useState<string | null>(null);

    // Load data on mount
    useEffect(() => {
        loadStats();
        loadParticipants();
        loadConfig();
    }, []);

    // Reload participants when filters change
    useEffect(() => {
        loadParticipants();
    }, [page, search, verifiedFilter, prizeFilter]);

    const loadStats = async () => {
        try {
            const data = await eventService.adminGetStats();
            setStats(data);
        } catch (error) {
            console.error('Error loading stats:', error);
        }
    };

    const loadParticipants = async () => {
        setLoading(true);
        try {
            const data = await eventService.adminListParticipants({
                page,
                per_page: perPage,
                search: search || undefined,
                verified: verifiedFilter as any || undefined,
                prize: prizeFilter || undefined
            });
            setParticipants(data.participants);
            setTotal(data.total);
        } catch (error: any) {
            showToast('Lỗi khi tải danh sách người tham gia', 'error');
        } finally {
            setLoading(false);
        }
    };

    const loadConfig = async () => {
        setConfigLoading(true);
        try {
            const data = await eventService.adminGetConfig();
            setConfig(data.config);
        } catch (error: any) {
            showToast('Lỗi khi tải cấu hình sự kiện', 'error');
        } finally {
            setConfigLoading(false);
        }
    };

    const handleSaveConfig = async () => {
        if (!config) return;

        setSavingConfig(true);
        try {
            await eventService.adminUpdateConfig(config);
            showToast('Cập nhật cấu hình thành công', 'success');
        } catch (error: any) {
            showToast(error.response?.data?.error || 'Lỗi khi cập nhật cấu hình', 'error');
        } finally {
            setSavingConfig(false);
        }
    };

    const handleVerify = async (id: number, type: 'fb' | 'youtube', verified: boolean) => {
        try {
            const data: any = {};
            if (type === 'fb') {
                data.admin_verified_fb = verified;
            } else {
                data.admin_verified_youtube = verified;
            }
            await eventService.adminVerify(id, data);
            showToast('Cập nhật xác nhận thành công', 'success');
            loadParticipants();
            loadStats();
        } catch (error: any) {
            showToast(error.response?.data?.error || 'Lỗi khi cập nhật xác nhận', 'error');
        }
    };

    const handleSetPrize = async (id: number, prizeType: string) => {
        try {
            await eventService.adminSetPrize(id, prizeType || null);
            showToast('Đặt giải thưởng thành công', 'success');
            loadParticipants();
        } catch (error: any) {
            showToast(error.response?.data?.error || 'Lỗi khi đặt giải thưởng', 'error');
        }
    };

    const handleDelete = async (id: number) => {
        if (!window.confirm('Bạn có chắc muốn xóa người tham gia này?')) return;

        try {
            await eventService.adminDeleteParticipant(id);
            showToast('Xóa thành công', 'success');
            loadParticipants();
            loadStats();
        } catch (error: any) {
            showToast(error.response?.data?.error || 'Lỗi khi xóa', 'error');
        }
    };

    const handleBulkDelete = async () => {
        if (selectedIds.length === 0) {
            showToast('Vui lòng chọn ít nhất một người tham gia', 'warning');
            return;
        }

        if (!window.confirm(`Bạn có chắc muốn xóa ${selectedIds.length} người tham gia?`)) return;

        try {
            await eventService.adminBulkDelete(selectedIds);
            showToast(`Đã xóa ${selectedIds.length} người tham gia`, 'success');
            setSelectedIds([]);
            setSelectAll(false);
            loadParticipants();
            loadStats();
        } catch (error: any) {
            showToast(error.response?.data?.error || 'Lỗi khi xóa hàng loạt', 'error');
        }
    };

    const handleSelectParticipant = (id: number) => {
        if (selectedIds.includes(id)) {
            setSelectedIds(selectedIds.filter(i => i !== id));
        } else {
            setSelectedIds([...selectedIds, id]);
        }
    };

    const handleSelectAll = () => {
        if (selectAll) {
            setSelectedIds([]);
            setSelectAll(false);
        } else {
            setSelectedIds(participants.map(p => p.id));
            setSelectAll(true);
        }
    };

    useEffect(() => {
        if (participants.length > 0) {
            const allSelected = participants.every(p => selectedIds.includes(p.id));
            setSelectAll(allSelected && selectedIds.length === participants.length);
        }
    }, [selectedIds, participants]);

    const totalPages = Math.ceil(total / perPage);

    const getPrizeLabel = (prizeType: string | null | undefined) => {
        switch (prizeType) {
            case 'first': return '🥇 Giải nhất';
            case 'second': return '🥈 Giải nhì';
            case 'third': return '🥉 Giải ba';
            case 'encouragement': return '🎁 Khuyến khích';
            default: return '';
        }
    };

    return (
        <div className="admin-events-page">
            <div className="page-header">
                <h1>Quản lý Sự kiện Năm Mới 2026</h1>
                <div className="header-actions">
                    {selectedIds.length > 0 && (
                        <button onClick={handleBulkDelete} className="btn btn-danger">
                            🗑️ Xóa ({selectedIds.length})
                        </button>
                    )}
                </div>
            </div>

            {/* Stats */}
            {stats && (
                <div className="stats-grid">
                    <div className="stat-card">
                        <div className="stat-value">{stats.total_participants}</div>
                        <div className="stat-label">Tổng người tham gia</div>
                    </div>
                    <div className="stat-card">
                        <div className="stat-value">{stats.returning_customers}</div>
                        <div className="stat-label">Khách quen</div>
                    </div>
                    <div className="stat-card">
                        <div className="stat-value">{stats.new_customers}</div>
                        <div className="stat-label">Khách lạ</div>
                    </div>
                    <div className="stat-card">
                        <div className="stat-value">{stats.fb_verified}</div>
                        <div className="stat-label">Đã xác nhận FB</div>
                    </div>
                    <div className="stat-card">
                        <div className="stat-value">{stats.youtube_verified}</div>
                        <div className="stat-label">Đã xác nhận YT</div>
                    </div>
                </div>
            )}

            {/* Tabs */}
            <div className="tabs">
                <button
                    className={`tab-btn ${activeTab === 'participants' ? 'active' : ''}`}
                    onClick={() => setActiveTab('participants')}
                >
                    👥 Người tham gia
                </button>
                <button
                    className={`tab-btn ${activeTab === 'config' ? 'active' : ''}`}
                    onClick={() => setActiveTab('config')}
                >
                    ⚙️ Cấu hình
                </button>
            </div>

            {/* Participants Tab */}
            {activeTab === 'participants' && (
                <>
                    {/* Filters */}
                    <div className="filters">
                        <input
                            type="text"
                            placeholder="Tìm kiếm theo tên hoặc SĐT..."
                            value={search}
                            onChange={e => {
                                setSearch(e.target.value);
                                setPage(1);
                            }}
                            className="search-input"
                        />
                        <select
                            value={verifiedFilter}
                            onChange={e => {
                                setVerifiedFilter(e.target.value);
                                setPage(1);
                            }}
                            className="filter-select"
                        >
                            <option value="">Tất cả xác nhận</option>
                            <option value="fb">Đã xác nhận FB</option>
                            <option value="youtube">Đã xác nhận YT</option>
                            <option value="both">Đã xác nhận cả hai</option>
                            <option value="none">Chưa xác nhận</option>
                        </select>
                        <select
                            value={prizeFilter}
                            onChange={e => {
                                setPrizeFilter(e.target.value);
                                setPage(1);
                            }}
                            className="filter-select"
                        >
                            <option value="">Tất cả giải</option>
                            <option value="first">Giải nhất</option>
                            <option value="second">Giải nhì</option>
                            <option value="third">Giải ba</option>
                            <option value="encouragement">Khuyến khích</option>
                        </select>
                    </div>

                    {/* Table */}
                    <div className="table-container">
                        {loading ? (
                            <div className="loading">Đang tải...</div>
                        ) : (
                            <table className="data-table">
                                <thead>
                                    <tr>
                                        <th style={{ width: '40px' }}>
                                            <input
                                                type="checkbox"
                                                checked={selectAll}
                                                onChange={handleSelectAll}
                                                title="Chọn tất cả"
                                            />
                                        </th>
                                        <th>Tên Zalo</th>
                                        <th>SĐT</th>
                                        <th>Loại khách</th>
                                        <th>Số đã chọn</th>
                                        <th>FB</th>
                                        <th>YouTube</th>
                                        <th>Giải thưởng</th>
                                        <th>Thời gian</th>
                                        <th>Thao tác</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {participants.map(p => (
                                        <tr key={p.id} className={selectedIds.includes(p.id) ? 'selected-row' : ''}>
                                            <td>
                                                <input
                                                    type="checkbox"
                                                    checked={selectedIds.includes(p.id)}
                                                    onChange={() => handleSelectParticipant(p.id)}
                                                />
                                            </td>
                                            <td><strong>{p.zalo_name}</strong></td>
                                            <td>{p.phone}</td>
                                            <td>
                                                <span className={`badge ${p.is_returning_customer ? 'returning' : 'new'}`}>
                                                    {p.is_returning_customer ? '⭐ Khách quen' : '👋 Khách lạ'}
                                                </span>
                                            </td>
                                            <td>
                                                <div className="numbers-list">
                                                    {p.chosen_numbers?.map((num, i) => (
                                                        <span key={i} className="number-badge">{num}</span>
                                                    ))}
                                                </div>
                                            </td>
                                            <td>
                                                {p.fb_screenshot ? (
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                                        <img
                                                            src={getMediaUrl(p.fb_screenshot)}
                                                            alt="FB"
                                                            className="screenshot-preview"
                                                            onClick={() => setPreviewImage(getMediaUrl(p.fb_screenshot!))}
                                                        />
                                                        <button
                                                            onClick={() => handleVerify(p.id, 'fb', !p.admin_verified_fb)}
                                                            className={`btn-icon`}
                                                            title={p.admin_verified_fb ? 'Bỏ xác nhận' : 'Xác nhận'}
                                                        >
                                                            {p.admin_verified_fb ? '✅' : '⬜'}
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <span style={{ color: '#999' }}>Chưa upload</span>
                                                )}
                                            </td>
                                            <td>
                                                {p.youtube_screenshot ? (
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                                        <img
                                                            src={getMediaUrl(p.youtube_screenshot)}
                                                            alt="YT"
                                                            className="screenshot-preview"
                                                            onClick={() => setPreviewImage(getMediaUrl(p.youtube_screenshot!))}
                                                        />
                                                        <button
                                                            onClick={() => handleVerify(p.id, 'youtube', !p.admin_verified_youtube)}
                                                            className={`btn-icon`}
                                                            title={p.admin_verified_youtube ? 'Bỏ xác nhận' : 'Xác nhận'}
                                                        >
                                                            {p.admin_verified_youtube ? '✅' : '⬜'}
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <span style={{ color: '#999' }}>Chưa upload</span>
                                                )}
                                            </td>
                                            <td>
                                                <select
                                                    value={p.prize_type || ''}
                                                    onChange={e => handleSetPrize(p.id, e.target.value)}
                                                    className="prize-select"
                                                >
                                                    <option value="">-- Chọn giải --</option>
                                                    <option value="first">🥇 Giải nhất</option>
                                                    <option value="second">🥈 Giải nhì</option>
                                                    <option value="third">🥉 Giải ba</option>
                                                    <option value="encouragement">🎁 Khuyến khích</option>
                                                </select>
                                            </td>
                                            <td>
                                                {p.submitted_at
                                                    ? new Date(p.submitted_at).toLocaleString('vi-VN')
                                                    : '-'}
                                            </td>
                                            <td>
                                                <div className="action-buttons">
                                                    <button
                                                        onClick={() => handleDelete(p.id)}
                                                        className="btn-icon"
                                                        title="Xóa"
                                                    >
                                                        🗑️
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>

                    {/* Pagination */}
                    {totalPages > 1 && (
                        <div className="pagination">
                            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>
                                Trước
                            </button>
                            <span>
                                Trang {page} / {totalPages} (Tổng: {total})
                            </span>
                            <button
                                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                                disabled={page === totalPages}
                            >
                                Sau
                            </button>
                        </div>
                    )}
                </>
            )}

            {/* Config Tab */}
            {activeTab === 'config' && (
                <div className="config-section">
                    {configLoading ? (
                        <div className="loading">Đang tải cấu hình...</div>
                    ) : config ? (
                        <div className="config-form">
                            <div className="form-group">
                                <label>Tên sự kiện</label>
                                <input
                                    type="text"
                                    value={config.event_name || ''}
                                    onChange={e => setConfig({ ...config, event_name: e.target.value })}
                                />
                            </div>
                            <div className="form-group">
                                <label>Mô tả sự kiện</label>
                                <textarea
                                    value={config.event_description || ''}
                                    onChange={e => setConfig({ ...config, event_description: e.target.value })}
                                />
                            </div>
                            <div className="form-group">
                                <label>Link Facebook Post</label>
                                <input
                                    type="url"
                                    placeholder="https://www.facebook.com/..."
                                    value={config.fb_post_link || ''}
                                    onChange={e => setConfig({ ...config, fb_post_link: e.target.value })}
                                />
                            </div>
                            <div className="form-group">
                                <label>Link YouTube Video</label>
                                <input
                                    type="url"
                                    placeholder="https://www.youtube.com/watch?v=..."
                                    value={config.youtube_video_link || ''}
                                    onChange={e => setConfig({ ...config, youtube_video_link: e.target.value })}
                                />
                            </div>
                            <div className="form-group">
                                <label>Trạng thái sự kiện</label>
                                <div className="toggle-container">
                                    <label className="toggle-switch">
                                        <input
                                            type="checkbox"
                                            checked={config.is_active}
                                            onChange={e => setConfig({ ...config, is_active: e.target.checked })}
                                        />
                                        <span className="toggle-slider"></span>
                                    </label>
                                    <span>{config.is_active ? '🟢 Đang hoạt động' : '🔴 Tạm dừng'}</span>
                                </div>
                            </div>
                            <div className="form-actions">
                                <button
                                    onClick={handleSaveConfig}
                                    className="btn btn-primary"
                                    disabled={savingConfig}
                                >
                                    {savingConfig ? 'Đang lưu...' : '💾 Lưu cấu hình'}
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div className="loading">Không thể tải cấu hình</div>
                    )}
                </div>
            )}

            {/* Image Preview Modal */}
            {previewImage && (
                <div className="modal-overlay" onClick={() => setPreviewImage(null)}>
                    <div className="modal-content" onClick={e => e.stopPropagation()}>
                        <h2>Xem minh chứng</h2>
                        <img src={previewImage} alt="Preview" />
                        <div className="form-actions">
                            <button onClick={() => setPreviewImage(null)} className="btn btn-secondary">
                                Đóng
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminEventsPage;
